import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePublishedAppVersion } from '../../../../services/modules/appVersion/appVersion.queries';
import { useNetworkContext } from '../../../offline/contexts/network/network.context';
import { hasReloadAttemptFor, reloadToVersion } from '../newAppVersionPopper.utils';

const CURRENT_APP_VERSION = import.meta.env.VITE_APP_VERSION;
const VERSION_CHECK_INTERVAL_IN_MS = 5 * 60 * 1000;
const MIN_INTERVAL_BETWEEN_VERSION_CHECKS_IN_MS = 10 * 1000;

export const useNewAppVersionPopper = () => {
  const history = useHistory();
  const { isOnline, isForcingOfflineMode } = useNetworkContext();
  const [isDismissed, setIsDismissed] = useState(false);

  const isVersionCheckEnabled = Boolean(CURRENT_APP_VERSION) && isOnline && !isForcingOfflineMode;

  const { data: publishedAppVersion, refetch: checkPublishedAppVersion } = usePublishedAppVersion({
    enabled: isVersionCheckEnabled,
    refetchInterval: VERSION_CHECK_INTERVAL_IN_MS,
    retry: false,
  });

  const publishedVersion = publishedAppVersion?.version ?? '';

  const hasNewAppVersion =
    Boolean(publishedVersion) &&
    publishedVersion !== CURRENT_APP_VERSION &&
    !hasReloadAttemptFor(publishedVersion);

  const reloadApp = () => reloadToVersion(publishedVersion);

  useEffect(() => {
    if (!isVersionCheckEnabled || hasNewAppVersion) {
      return;
    }

    let lastCheckAt = Date.now();
    let lastPathname = history.location.pathname;

    const checkWhenNotRecentlyChecked = () => {
      if (Date.now() - lastCheckAt < MIN_INTERVAL_BETWEEN_VERSION_CHECKS_IN_MS) {
        return;
      }

      lastCheckAt = Date.now();
      checkPublishedAppVersion();
    };

    const stopListeningToNavigation = history.listen(({ pathname }) => {
      if (pathname === lastPathname) {
        return;
      }

      lastPathname = pathname;
      checkWhenNotRecentlyChecked();
    });

    window.addEventListener('focus', checkWhenNotRecentlyChecked);

    return () => {
      stopListeningToNavigation();
      window.removeEventListener('focus', checkWhenNotRecentlyChecked);
    };
  }, [isVersionCheckEnabled, hasNewAppVersion, history, checkPublishedAppVersion]);

  return {
    isOpen: hasNewAppVersion && !isDismissed,
    reloadApp,
    dismiss: () => setIsDismissed(true),
  };
};
